import { Box } from "@mui/material";
import BaseTypography from "@ui/atoms/BaseTypography/BaseTypography";

/**
 * A short string rendered as a bordered tile, for slots that take an icon but
 * whose items a single shared glyph cannot tell apart.
 *
 * Deliberately has no idea what it is labelling. The collapsed sidebar's
 * compliance items are the first caller — every one of them rendered the same
 * `file-check` glyph, so a project's frameworks were indistinguishable — but
 * the domain semantics stay at the call site, which is why this takes a plain
 * `label` and not a framework.
 *
 * The TILE is a fixed size and the TYPE steps down inside it. Those two rules
 * work together: a tile that grew with its label made a column of them ragged —
 * "GS" beside "VIPP" beside "IPP" read as five different controls rather than
 * one kind of thing — and a single font size in a fixed tile either wastes the
 * two-character case or overflows the long ones.
 *
 * The tile is sized for a THREE-character label, which is what most of the
 * sidebar's framework codes are. Longer ones still render, progressively
 * smaller, so nothing breaks; past five characters they are simply not legible
 * at this size, and the answer to that is a shorter code, not a wider tile.
 *
 * The tile always draws its border, and a caller that wants it gone turns it
 * off in CSS through the `data-monogram` hook rather than by passing state
 * down: the collapsed sidebar rail does exactly that. This component may not
 * read `@hooks/context` — it is design system, and an atom that reads a
 * provider cannot be rendered from a story or another shell without dragging
 * the session behind it.
 */
const fontSizeForLength = (length: number): string => {
  if (length <= 3) return "10px";
  if (length === 4) return "8.5px";
  if (length === 5) return "7.5px";
  return "6.5px";
};

export interface MonogramProps {
  label: string;
  /** Matches the prop `applyIconProps` injects into an icon slot. */
  color?: string;
  dataTestId?: string;
}

export const Monogram = ({ color, dataTestId, label }: MonogramProps) => {
  return (
    <Box
      data-monogram=""
      data-test-id={dataTestId}
      sx={{
        alignItems: "center",
        border: "1px solid currentColor",
        borderRadius: "6px",
        boxSizing: "border-box",
        color: color ?? "inherit",
        display: "inline-flex",
        flexShrink: 0,
        height: 24,
        justifyContent: "center",
        // FIXED, not `minWidth`: every tile is the same box whatever it holds, so
        // a column of them shares one left and one right edge. This is the whole
        // reason the component exists in a nav rail rather than being a chip.
        overflow: "hidden",
        width: 30
      }}
    >
      <BaseTypography
        sx={{
          fontSize: fontSizeForLength(label.length),
          letterSpacing: "0.2px",
          lineHeight: 1,
          whiteSpace: "nowrap"
        }}
        text={label}
        weight="semiBold"
        variant="caption"
      />
    </Box>
  );
};
